import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  InputGroup,
  ListGroup,
  Row,
  Spinner,
} from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import { customerRequestOptions } from "../../services/customerService";

const API_BASE_URL = (
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_POS_API_BASE_URL ||
  "https://doticom.vn:3000"
).replace(/\/+$/, "");
const VIETQR_BANKS_URL = "https://vietqr.app/banks.json";
const STORE_FIELDS = ["NganHang", "STKNganHang", "SoDienThoai", "DiaChi"];
const EMPTY_FORM = {
  NganHang: "",
  STKNganHang: "",
  SoDienThoai: "",
  DiaChi: "",
};

const getStoreId = (store) => store?.IDCH ?? store?.MaCH ?? store?.id;

const getStoreName = (store) =>
  store?.TenCuaHang || store?.TenCH || `Cửa hàng ${getStoreId(store)}`;

const normalizeSearchText = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

const matchesBankValue = (bank, value) =>
  [bank.code, bank.short_name, bank.name, ...(bank.alias || [])].some(
    (bankValue) => normalizeSearchText(bankValue) === normalizeSearchText(value)
  );

const getErrorMessage = (response, result) => {
  if (result?.message) {
    return result.error ? `${result.message}: ${result.error}` : result.message;
  }

  if (response.status === 400) return "storeId hoặc thông tin cập nhật không hợp lệ";
  if (response.status === 404) return "Không tìm thấy cửa hàng với ID đã cho";
  if (response.status >= 500) return "Lỗi server khi cập nhật thông tin cửa hàng";
  return "Không thể cập nhật thông tin cửa hàng";
};

const StoreInfo = () => {
  const [stores, setStores] = useState([]);
  const [selectedStoreId, setSelectedStoreId] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [initialForm, setInitialForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [banks, setBanks] = useState([]);
  const [bankSearch, setBankSearch] = useState("");
  const [bankLoading, setBankLoading] = useState(true);
  const [bankError, setBankError] = useState("");
  const [bankDropdownOpen, setBankDropdownOpen] = useState(false);

  const selectedStore = useMemo(
    () => stores.find((store) => String(getStoreId(store)) === String(selectedStoreId)),
    [selectedStoreId, stores]
  );

  const filteredBanks = useMemo(() => {
    const searchText = normalizeSearchText(bankSearch);
    return banks
      .filter((bank) =>
        [bank.short_name, bank.code, bank.name, ...(bank.alias || [])].some((value) =>
          normalizeSearchText(value).includes(searchText)
        )
      )
      .slice(0, 10);
  }, [bankSearch, banks]);

  const readStore = (store) => {
    const nextForm = STORE_FIELDS.reduce((values, field) => {
      values[field] = store?.[field] == null ? "" : String(store[field]);
      return values;
    }, {});
    setForm(nextForm);
    setInitialForm(nextForm);
  };

  const fetchStores = async () => {
    setLoading(true);
    setMessage(null);

    try {
      const response = await fetch(`${API_BASE_URL}/stores`);
      const result = await response.json();
      if (!response.ok || !Array.isArray(result)) {
        throw new Error(result?.message || "Không thể tải danh sách cửa hàng");
      }

      setStores(result);
      const savedStoreId = localStorage.getItem("IDCH") || localStorage.getItem("MaCH");
      const defaultStore =
        result.find((store) => String(getStoreId(store)) === String(savedStoreId)) || result[0];
      if (defaultStore) {
        setSelectedStoreId(String(getStoreId(defaultStore)));
        readStore(defaultStore);
      }
    } catch (error) {
      setMessage({ type: "error", text: error.message || "Không thể tải danh sách cửa hàng" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStores();
  }, []);

  useEffect(() => {
    const fetchBanks = async () => {
      setBankLoading(true);
      setBankError("");
      try {
        const response = await fetch(VIETQR_BANKS_URL);
        const result = await response.json();
        if (!response.ok || !Array.isArray(result?.data)) {
          throw new Error("Không thể tải danh sách ngân hàng từ VietQR");
        }
        setBanks(result.data);
      } catch (error) {
        setBankError(error.message || "Không thể tải danh sách ngân hàng từ VietQR");
      } finally {
        setBankLoading(false);
      }
    };

    fetchBanks();
  }, []);

  useEffect(() => {
    const selectedBank = banks.find((bank) => matchesBankValue(bank, form.NganHang));
    setBankSearch(selectedBank?.short_name || form.NganHang || "");
  }, [banks, form.NganHang]);

  const handleStoreChange = (event) => {
    const nextStoreId = event.target.value;
    setSelectedStoreId(nextStoreId);
    const nextStore = stores.find(
      (store) => String(getStoreId(store)) === String(nextStoreId)
    );
    if (nextStore) readStore(nextStore);
    setMessage(null);
  };

  const handleFieldChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    setMessage(null);
  };

  const handleBankSelect = (bank) => {
    setForm((current) => ({ ...current, NganHang: bank.code }));
    setBankSearch(bank.short_name);
    setBankDropdownOpen(false);
    setMessage(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!selectedStoreId) {
      setMessage({ type: "error", text: "Vui lòng chọn cửa hàng cần cập nhật" });
      return;
    }

    const payload = STORE_FIELDS.reduce((values, field) => {
      if (form[field] !== initialForm[field]) values[field] = String(form[field] || "").trim();
      return values;
    }, {});

    if (Object.keys(payload).length === 0) {
      setMessage({ type: "info", text: "Chưa có thông tin nào thay đổi" });
      return;
    }

    setSaving(true);
    setMessage(null);
    try {
      const response = await fetch(`${API_BASE_URL}/stores/${encodeURIComponent(selectedStoreId)}`, {
        method: "PUT",
        // PUT /stores/:id requires a signed admin session.
        headers: customerRequestOptions({ headers: { "Content-Type": "application/json" } }).headers,
        body: JSON.stringify(payload),
      });
      const result = await response.json().catch(() => ({}));

      if (!response.ok || result.success === false) {
        throw new Error(getErrorMessage(response, result));
      }

      const updatedFields = result.updatedFields?.join(", ") || Object.keys(payload).join(", ");
      setInitialForm(form);
      setMessage({
        type: "success",
        text: `${result.message || "Cập nhật thông tin cửa hàng thành công"}. Field đã cập nhật: ${updatedFields}`,
      });
    } catch (error) {
      setMessage({ type: "error", text: error.message || "Lỗi server khi cập nhật thông tin cửa hàng" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container fluid="lg" className="py-4">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
        <div>
          <h1 className="h3 mb-1">Thông tin cửa hàng</h1>
          <p className="text-muted mb-0">Cập nhật thông tin thanh toán, liên hệ và địa chỉ.</p>
        </div>
        <Button type="button" variant="outline-secondary" onClick={fetchStores} disabled={loading || saving}>
          {loading && <Spinner as="span" size="sm" animation="border" className="me-2" />}
          Làm mới
        </Button>
      </div>

      <Card className="shadow-sm">
        <Card.Header className="bg-white py-3">
          <Row className="align-items-end g-3">
            <Col md>
              <div className="fw-semibold">{selectedStore ? getStoreName(selectedStore) : "Chọn cửa hàng"}</div>
              {selectedStore && <small className="text-muted">Mã cửa hàng: {selectedStoreId}</small>}
            </Col>
            {!loading && stores.length > 0 && (
              <Col md={5} lg={4}>
                <Form.Label className="small text-muted mb-1">Cửa hàng</Form.Label>
                <Form.Select value={selectedStoreId} onChange={handleStoreChange} disabled={saving}>
                  {stores.map((store) => (
                    <option key={String(getStoreId(store))} value={String(getStoreId(store))}>
                      {getStoreName(store)} — ID {getStoreId(store)}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            )}
          </Row>
        </Card.Header>

        <Card.Body className="p-4">
          {message && <Alert variant={message.type === "success" ? "success" : message.type === "error" ? "danger" : "info"}>{message.text}</Alert>}

          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" role="status" /><p className="text-muted mt-3 mb-0">Đang tải danh sách cửa hàng...</p></div>
          ) : stores.length === 0 ? (
            <Alert variant="warning" className="mb-0">Không có cửa hàng để cập nhật.</Alert>
          ) : (
            <Form onSubmit={handleSubmit}>
              <Row className="g-4">
                <Col md={6}>
                  <Form.Group className="position-relative">
                    <Form.Label>Ngân hàng nhận tiền</Form.Label>
                    <InputGroup>
                      <InputGroup.Text>Tìm</InputGroup.Text>
                  <Form.Control
                    value={bankSearch}
                    onChange={(event) => {
                      setBankSearch(event.target.value);
                      setBankDropdownOpen(true);
                    }}
                    onFocus={() => setBankDropdownOpen(true)}
                    onBlur={() => setBankDropdownOpen(false)}
                    placeholder={bankLoading ? "Đang tải danh sách ngân hàng..." : "Tìm theo tên hoặc mã ngân hàng"}
                    disabled={saving || bankLoading || Boolean(bankError)}
                    aria-label="Tìm kiếm ngân hàng"
                  />
                    </InputGroup>
                  {bankDropdownOpen && !bankLoading && !bankError && (
                    <ListGroup className="position-absolute top-100 start-0 w-100 mt-1 shadow" style={{ zIndex: 10 }}>
                      {filteredBanks.length > 0 ? (
                        filteredBanks.map((bank) => (
                          <ListGroup.Item
                            action
                            type="button"
                            key={bank.code}
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => handleBankSelect(bank)}
                          >
                            <div className="fw-semibold">{bank.short_name}</div>
                            <small className="text-muted">{bank.code} — {bank.name}</small>
                          </ListGroup.Item>
                        ))
                      ) : (
                        <ListGroup.Item className="text-muted">Không tìm thấy ngân hàng phù hợp.</ListGroup.Item>
                      )}
                    </ListGroup>
                  )}
                    {bankError ? <Form.Text className="text-danger">{bankError}</Form.Text> : <Form.Text>Mã sẽ lưu: <Badge bg="secondary">{form.NganHang || "Chưa chọn"}</Badge></Form.Text>}
                  </Form.Group>
                </Col>
                <Col md={6}><Form.Group><Form.Label>Số tài khoản ngân hàng</Form.Label><Form.Control name="STKNganHang" value={form.STKNganHang} onChange={handleFieldChange} placeholder="Ví dụ: 0123456789" disabled={saving} /></Form.Group></Col>
                <Col md={6}><Form.Group><Form.Label>Số điện thoại liên hệ</Form.Label><Form.Control name="SoDienThoai" value={form.SoDienThoai} onChange={handleFieldChange} placeholder="Ví dụ: 0905123456" disabled={saving} /></Form.Group></Col>
                <Col xs={12}><Form.Group><Form.Label>Địa chỉ cửa hàng</Form.Label><Form.Control as="textarea" name="DiaChi" value={form.DiaChi} onChange={handleFieldChange} rows={4} placeholder="Số nhà, đường, phường/xã, quận/huyện, tỉnh/thành phố" disabled={saving} /></Form.Group></Col>
              </Row>
              <div className="d-flex justify-content-end mt-4 pt-3 border-top">
                <Button type="submit" variant="primary" disabled={saving}>{saving && <Spinner as="span" size="sm" animation="border" className="me-2" />}{saving ? "Đang cập nhật..." : "Cập nhật thông tin"}</Button>
              </div>
            </Form>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default StoreInfo;
